'use client';
import { useEffect, useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';

type Tier = 'Q-direct' | 'Q-flow' | 'FYI';

interface PortfolioEvent {
  ts: string;
  tier: Tier;
  type: string;
  symbol: string;
  headline: string;
  sourceUrl?: string;
  detail: Record<string, unknown>;
  context: { q: number | null; v: number | null; sector: string | null };
}

const TIER_COLOR: Record<Tier, string> = {
  'Q-direct': '#c9a84c', // gold
  'Q-flow': '#3b82f6', // blue
  FYI: '#64748b', // muted gray
};

const TIER_LABEL: Record<Tier, string> = {
  'Q-direct': 'Q',
  'Q-flow': 'Q∙',
  FYI: 'FYI',
};

const POLL_INTERVAL_MS = 60_000;
const FETCH_LIMIT = 500;
const RETENTION_DAYS = 7;

function fmtRelative(ts: string): string {
  const eventTime = Date.parse(ts);
  if (Number.isNaN(eventTime)) return '';
  const diffMs = Date.now() - eventTime;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(eventTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtSigma(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}σ`;
}

interface Props {
  onSelectSymbol?: (symbol: string) => void;
  /** Override the symbol set the feed filters by. Defaults to the portfolio.
   *  Pass e.g. the Alpha Zone basket to get a parallel feed restricted to
   *  those names instead. */
  symbols?: Set<string> | string[] | null;
  /** Header label for the panel. Defaults to "Portfolio Alerts". */
  title?: string;
  /** Compact mode shrinks fonts + padding ~30% for embedded overlays
   *  (used by the AlphaZoneVisualizer's bottom-left alerts box). */
  compact?: boolean;
  /** Max-height for the scroll area. Defaults to 420px (or 220px in compact). */
  maxHeightPx?: number;
}

export function PortfolioEventsFeed({ onSelectSymbol, symbols, title = 'Portfolio Alerts', compact = false, maxHeightPx }: Props = {}) {
  const fs = (norm: string, cmp: string) => compact ? cmp : norm;
  const padX = compact ? 'px-2' : 'px-5';
  const padY = compact ? 'py-1.5' : 'py-3';
  const innerPadX = compact ? 'px-2' : 'px-3';
  // py-1 (not 1.5) for non-compact: tighter rows fit ~20% more events per
  // panel and the sector tag now lives inline on the headline row, saving
  // another ~14px per row.
  const innerPadY = compact ? 'py-0.5' : 'py-1';
  const maxH = maxHeightPx ?? (compact ? 220 : 420);
  const [result, setResult] = useState<{ pollTs: number; events: PortfolioEvent[] } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  // Default symbol set comes from the portfolio API. Shared via useResource
  // so other components hitting /api/portfolio don't re-fetch. The `symbols`
  // prop overrides this when present (e.g. Alpha Zone basket).
  const { data: portfolioData } = useResource<{ symbols?: string[] }>(
    symbols == null ? '/api/portfolio' : null,
  );
  const portfolioSet = useMemo(() => {
    if (symbols != null) {
      return symbols instanceof Set ? symbols : new Set(symbols);
    }
    return new Set(portfolioData?.symbols ?? []);
  }, [symbols, portfolioData]);

  // Re-fetch every POLL_INTERVAL_MS
  useEffect(() => {
    const id = setInterval(() => setPollTick((p) => p + 1), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Fetch on mount + each poll tick. Server-side filters to portfolio symbols
  // and the 7-day retention window so the panel never gets pushed off by
  // universe-wide reporters (Apr 28 had 151 events / 10 portfolio — old
  // client-side filter on the latest-30 returned an empty panel).
  useEffect(() => {
    if (portfolioSet.size === 0) return; // wait for portfolio to load
    let cancelled = false;
    const symbols = [...portfolioSet].join(',');
    fetch(`/api/portfolio-events?limit=${FETCH_LIMIT}&days=${RETENTION_DAYS}&symbols=${encodeURIComponent(symbols)}`)
      .then((r) => {
        if (!r.ok) throw new Error('API error');
        return r.json();
      })
      .then((d: { events?: PortfolioEvent[] }) => {
        if (cancelled) return;
        const events = Array.isArray(d?.events) ? d.events : [];
        setResult({ pollTs: pollTick, events });
      })
      .catch(() => {
        if (!cancelled) setResult({ pollTs: pollTick, events: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [pollTick, portfolioSet]);

  const events = result?.events ?? [];
  const loading = !result;

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCount = events.filter((e) => e.ts.slice(0, 10) === todayStr).length;

  return (
    <div className={compact ? '' : 'border-t-2 border-[#1e293b]'}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`w-full flex items-center justify-between ${padX} ${padY} hover:bg-[#1e293b]/30 transition-colors`}
        title={loading ? 'loading...' : `${events.length} recent events, ${todayCount} today`}
      >
        <span className={`${fs('text-[10px]', 'text-[8px]')} font-semibold uppercase tracking-widest text-[#94a3b8]`}>
          {title}
        </span>
        <span className={`${fs('text-[10px]', 'text-[7px]')} text-[#64748b]`}>
          {loading ? '…' : `${todayCount} today · ${events.length} (7d)`}
          <span className="ml-2 text-[#475569]">{collapsed ? '▸' : '▾'}</span>
        </span>
      </button>

      {!collapsed && (
        <div style={{ maxHeight: maxH, overflowY: 'auto' }}>
          {loading && events.length === 0 ? (
            <div className={`${padX} ${padY} ${fs('text-[11px]', 'text-[9px]')} text-[#64748b]`}>Loading events…</div>
          ) : events.length === 0 ? (
            <div className={`${padX} ${padY} ${fs('text-[11px]', 'text-[9px]')} text-[#64748b]`}>
              No recent events. The cron checks every 30 min during market hours.
            </div>
          ) : (
            events.map((e, i) => {
              const accent = TIER_COLOR[e.tier] ?? '#64748b';
              const isExpanded = expanded === `${e.ts}-${i}`;
              return (
                <div
                  key={`${e.ts}-${i}`}
                  className="border-b border-[#1e293b]/60 hover:bg-[#1e293b]/20 transition-colors"
                  style={{ borderLeft: `3px solid ${accent}` }}
                >
                  <div className={`${innerPadX} ${innerPadY}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <button
                        onClick={() => {
                          setExpanded(isExpanded ? null : `${e.ts}-${i}`);
                          onSelectSymbol?.(e.symbol);
                        }}
                        className={`${fs('text-[12px]', 'text-[10px]')} font-semibold text-[#e2e8f0] hover:text-white text-left`}
                      >
                        {e.symbol}
                      </button>
                      <span
                        className={`${fs('text-[9px]', 'text-[7px]')} uppercase tracking-wider font-bold`}
                        style={{ color: accent }}
                      >
                        {TIER_LABEL[e.tier] ?? e.tier}
                      </span>
                      <span className={`${fs('text-[9px]', 'text-[7px]')} text-[#64748b] flex gap-1.5 ml-2 whitespace-nowrap`}>
                        {e.context?.q !== null && e.context?.q !== undefined && (
                          <span>Q={fmtSigma(e.context.q)}</span>
                        )}
                        {e.context?.v !== null && e.context?.v !== undefined && (
                          <span>V={fmtSigma(e.context.v)}</span>
                        )}
                      </span>
                      <span className={`${fs('text-[10px]', 'text-[7px]')} text-[#64748b] ml-auto whitespace-nowrap flex items-baseline gap-1.5`}>
                        {fmtRelative(e.ts)}
                        {!compact && e.context?.sector && (
                          <button
                            onClick={() => {
                              setExpanded(isExpanded ? null : `${e.ts}-${i}`);
                              onSelectSymbol?.(e.symbol);
                            }}
                            className="text-[9px] text-[#475569] hover:text-[#94a3b8] whitespace-nowrap"
                            title="Sector"
                          >
                            · {e.context.sector}
                          </button>
                        )}
                      </span>
                    </div>
                    {e.sourceUrl ? (
                      <a
                        href={e.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`block ${fs('text-[11px]', 'text-[9px]')} text-[#cbd5e1] hover:text-white leading-tight underline-offset-2 hover:underline`}
                      >
                        {e.headline} <span className={`${fs('text-[9px]', 'text-[7px]')} text-[#64748b]`}>↗</span>
                      </a>
                    ) : (
                      <div className={`${fs('text-[11px]', 'text-[9px]')} text-[#cbd5e1] leading-tight`}>
                        {e.headline}
                      </div>
                    )}
                  </div>
                  {isExpanded && e.detail && (
                    <div className="px-3 pb-2 text-[10px] text-[#94a3b8] font-mono whitespace-pre-wrap break-words">
                      {Object.entries(e.detail)
                        .filter(([k, v]) => v !== null && v !== undefined && v !== '' && k !== 'symbol')
                        .map(([k, v]) => (
                          <div key={k}>
                            <span className="text-[#64748b]">{k}:</span>{' '}
                            <span className="text-[#cbd5e1]">
                              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                            </span>
                          </div>
                        ))}
                      {typeof e.detail.url === 'string' && e.detail.url && (
                        <a
                          href={e.detail.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#3b82f6] underline mt-1 inline-block"
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          source ↗
                        </a>
                      )}
                      {typeof e.detail.link === 'string' && e.detail.link && (
                        <a
                          href={e.detail.link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#3b82f6] underline mt-1 inline-block"
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          source ↗
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
